'use client';

import React, { useContext, useEffect, useState, useCallback } from 'react';
import { BuildingsIcon, PencilSimpleIcon, PlusIcon, ProhibitIcon } from '@phosphor-icons/react';
import { Card, CardBody, CardHeader } from '@heroui/card';
import { Button } from '@heroui/button';
import { Spinner } from '@heroui/spinner';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure } from '@heroui/modal';
import { AuthContext, ToastContext } from '@/app/contexts';
import { ToastLevel } from '@/components/ui/toast';
import { useCSM } from '@/contexts/CSMContext';
import { listOrganizations, createOrganization, getOrganization, updateOrganization, updateCurrentOrganization, setTenantPoolStateReady, setTenantPoolStateNotReady } from '@/lib/admin-organizations';
import { createTenant } from '@/lib/admin-tenants';
import { getTokenForHeimdallAdmin } from '@/lib/heimdall-admin-token';
import { CreateOrganizationForm } from './CreateOrganizationForm';
import { EditOrganizationForm } from './EditOrganizationForm';
import { CreateTenantForm } from './CreateTenantForm';
import type { OrganizationDTO, CreateOrganizationRequest, TenantDTO } from '@/shared/types';
function statusMessageClassName(statusClass: string | null | undefined): string {
  switch (statusClass) {
    case 'success':
      return 'text-green-400 font-medium';
    case 'warning':
      return 'text-amber-400 font-medium';
    case 'muted':
      return 'text-slate-500';
    default:
      return 'text-slate-400';
  }
}
export function OrganizationsTab() {
  const {
    idToken
  } = useContext(AuthContext);
  const toastContext = useContext(ToastContext);
  const {
    canManageOrgs,
    isSuperAdmin,
    isLoaded
  } = useCSM();
  const [organizations, setOrganizations] = useState<OrganizationDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const {
    isOpen,
    onOpen,
    onOpenChange
  } = useDisclosure();
  const [editOrgId, setEditOrgId] = useState<string | null>(null);
  const [editOrg, setEditOrg] = useState<OrganizationDTO | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [createTenantOrg, setCreateTenantOrg] = useState<OrganizationDTO | null>(null);
  const [orgToSetCurrent, setOrgToSetCurrent] = useState<OrganizationDTO | null>(null);
  const [setCurrentLoading, setSetCurrentLoading] = useState(false);
  const [orgToSetReady, setOrgToSetReady] = useState<OrganizationDTO | null>(null);
  const [setReadyLoading, setSetReadyLoading] = useState(false);
  const [orgToSetNotReady, setOrgToSetNotReady] = useState<OrganizationDTO | null>(null);
  const [setNotReadyLoading, setSetNotReadyLoading] = useState(false);
  const fetchList = useCallback(async () => {
    const token = await getTokenForHeimdallAdmin(idToken ?? null);
    if (!token) {
      setLoading(false);
      setError('Employee token unavailable. Switch tenant or refresh the page, then try again.');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const res = await listOrganizations(token, 0, 100);
      setOrganizations(res.organizations ?? []);
      setTotal(res.total ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, [idToken]);
  useEffect(() => {
    if (isLoaded && (isSuperAdmin || canManageOrgs) && idToken) {
      fetchList();
    }
  }, [canManageOrgs, isLoaded, isSuperAdmin, idToken, fetchList]);
  const handleCreateSuccess = (org: OrganizationDTO) => {
    fetchList();
    if (toastContext?.setToast && toastContext?.setToastOpen) {
      toastContext.setToast({
        setOpen: toastContext.setToastOpen,
        title: 'Organization created',
        description: org.name,
        level: ToastLevel.SUCCESS,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 3000
      });
      toastContext.setToastOpen(true);
    }
  };
  const handleSubmitCreate = async (data: CreateOrganizationRequest) => {
    const token = await getTokenForHeimdallAdmin(idToken ?? null);
    if (!token) throw new Error('Not authenticated');
    return createOrganization(token, data);
  };
  const handleSubmitCreateTenant = async (data: Parameters<typeof createTenant>[1]) => {
    const token = await getTokenForHeimdallAdmin(idToken ?? null);
    if (!token) throw new Error('Not authenticated');
    return createTenant(token, data);
  };
  const handleCreateTenantSuccess = (tenant: TenantDTO) => {
    setCreateTenantOrg(null);
    fetchList();
    if (toastContext?.setToast && toastContext?.setToastOpen) {
      toastContext.setToast({
        setOpen: toastContext.setToastOpen,
        title: 'Tenant created',
        description: tenant.tenant_name,
        level: ToastLevel.SUCCESS,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 3000
      });
      toastContext.setToastOpen(true);
    }
  };
  useEffect(() => {
    if (!editOrgId) return;
    let cancelled = false;
    setEditOrg(null);
    setEditError(null);
    setEditLoading(true);
    getTokenForHeimdallAdmin(idToken ?? null).then(token => {
      if (cancelled) return;
      if (!token) {
        setEditError('Not authenticated. Please refresh and try again.');
        return;
      }
      return getOrganization(token, editOrgId).then(o => {
        if (!cancelled) setEditOrg(o);
      }).catch(err => {
        if (!cancelled) setEditError(err instanceof Error ? err.message : 'Failed to load organization');
      });
    }).finally(() => {
      if (!cancelled) setEditLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [editOrgId, idToken]);
  const handleEditSuccess = (org: OrganizationDTO) => {
    setEditOrgId(null);
    setEditOrg(null);
    setEditError(null);
    fetchList();
    if (toastContext?.setToast && toastContext?.setToastOpen) {
      toastContext.setToast({
        setOpen: toastContext.setToastOpen,
        title: 'Organization updated',
        description: org.name,
        level: ToastLevel.SUCCESS,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 3000
      });
      toastContext.setToastOpen(true);
    }
  };
  const handleSubmitEdit = async (orgId: string, data: Parameters<typeof updateOrganization>[2]) => {
    const token = await getTokenForHeimdallAdmin(idToken ?? null);
    if (!token) throw new Error('Not authenticated');
    return updateOrganization(token, orgId, data);
  };
  const handleUpdateCurrentOrganization = async (orgId: string) => {
    const token = await getTokenForHeimdallAdmin(idToken ?? null);
    if (!token) throw new Error('Not authenticated');
    return updateCurrentOrganization(token, orgId);
  };
  const handleConfirmSetReady = async (onClose: () => void) => {
    setSetReadyLoading(true);
    try {
      const token = await getTokenForHeimdallAdmin(idToken ?? null);
      if (!token) throw new Error('Not authenticated');
      await setTenantPoolStateReady(token);
      setOrgToSetReady(null);
      onClose();
      fetchList();
      if (toastContext?.setToast && toastContext?.setToastOpen) {
        toastContext.setToast({
          setOpen: toastContext.setToastOpen,
          title: 'Organization ready for tenants',
          description: 'The current organization is now marked as ready. You can create tenants.',
          level: ToastLevel.SUCCESS,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 3000
        });
        toastContext.setToastOpen(true);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to set organization as ready';
      if (toastContext?.setToast && toastContext?.setToastOpen) {
        toastContext.setToast({
          setOpen: toastContext.setToastOpen,
          title: 'Update failed',
          description: message,
          level: ToastLevel.ERROR,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 5000
        });
        toastContext.setToastOpen(true);
      }
    } finally {
      setSetReadyLoading(false);
    }
  };
  const handleConfirmSetNotReady = async (onClose: () => void) => {
    setSetNotReadyLoading(true);
    try {
      const token = await getTokenForHeimdallAdmin(idToken ?? null);
      if (!token) throw new Error('Not authenticated');
      await setTenantPoolStateNotReady(token);
      setOrgToSetNotReady(null);
      onClose();
      fetchList();
      if (toastContext?.setToast && toastContext?.setToastOpen) {
        toastContext.setToast({
          setOpen: toastContext.setToastOpen,
          title: 'Organization not ready for tenants',
          description: 'The current organization is now marked as not ready.',
          level: ToastLevel.SUCCESS,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 3000
        });
        toastContext.setToastOpen(true);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to set organization as not ready';
      if (toastContext?.setToast && toastContext?.setToastOpen) {
        toastContext.setToast({
          setOpen: toastContext.setToastOpen,
          title: 'Update failed',
          description: message,
          level: ToastLevel.ERROR,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 5000
        });
        toastContext.setToastOpen(true);
      }
    } finally {
      setSetNotReadyLoading(false);
    }
  };
  const handleConfirmSetCurrent = async (onClose: () => void) => {
    if (!orgToSetCurrent) return;
    setSetCurrentLoading(true);
    try {
      await handleUpdateCurrentOrganization(orgToSetCurrent.id);
      const name = orgToSetCurrent.name;
      setOrgToSetCurrent(null);
      onClose();
      fetchList();
      if (toastContext?.setToast && toastContext?.setToastOpen) {
        toastContext.setToast({
          setOpen: toastContext.setToastOpen,
          title: 'Current organization updated',
          description: `${name} is now the current organization.`,
          level: ToastLevel.SUCCESS,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 3000
        });
        toastContext.setToastOpen(true);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update current organization';
      if (toastContext?.setToast && toastContext?.setToastOpen) {
        toastContext.setToast({
          setOpen: toastContext.setToastOpen,
          title: 'Update failed',
          description: message,
          level: ToastLevel.ERROR,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 5000
        });
        toastContext.setToastOpen(true);
      }
    } finally {
      setSetCurrentLoading(false);
    }
  };
  if (!isLoaded || !(isSuperAdmin || canManageOrgs)) {
    return <Card classNames={{
      base: 'bg-white/5 border-white/10',
      body: 'text-center py-12'
    }}>
        <CardBody>
          <Spinner size="lg" color="secondary" />
          <p className="text-gray-300 mt-4">Loading...</p>
        </CardBody>
      </Card>;
  }
  return <>
      <div className="flex flex-col gap-4">
        {loading ? <Card classNames={{
        base: 'bg-white/5 border-white/10',
        body: 'text-center flex flex-col items-center justify-center gap-2 py-12'
      }}>
            <CardBody>
              <Spinner size="lg" color="secondary" variant="gradient" />
              <p className="text-gray-300">Loading organizations...</p>
            </CardBody>
          </Card> : error ? <Card className="w-full bg-white/5 border-white/10">
            <CardBody className="flex flex-col items-center justify-center text-center">
              <p role="alert" className="text-red-400 mb-4">
                Error loading organizations: {error}
              </p>
              <Button color="primary" onPress={fetchList} className="w-fit text-white hover:bg-white/10 cursor-pointer">
                Retry
              </Button>
            </CardBody>
          </Card> : organizations.length === 0 ? <Card className="max-w-full overflow-visible bg-white/5 border-white/10">
            <CardBody className="text-center">
              <BuildingsIcon size={64} className="text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Organizations</h3>
              <p className="text-gray-300 mb-6">
                Create your first organization to get started.
              </p>
              <Button color="primary" onPress={onOpen} startContent={<PlusIcon size={20} />} className="cursor-pointer text-white">
                Create Organization
              </Button>
            </CardBody>
          </Card> : <Card classNames={{
        base: 'box-border bg-white/5 border-white/10 p-4 rounded-lg h-[calc(100vh-300px)] flex flex-col',
        header: 'box-border flex justify-between items-center bg-white/5 p-4 rounded-lg',
        body: 'box-border pt-6 overflow-y-auto min-h-0 flex-1'
      }}>
            <CardHeader className="flex justify-between items-center">
              <span className="text-white font-medium">Organizations ({total})</span>
              <Button color="primary" size="sm" onPress={onOpen} startContent={<PlusIcon size={18} />} className="cursor-pointer text-white">
                Create Organization
              </Button>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-1 gap-4">
                {organizations.map(org => <div key={org.id} role="button" tabIndex={0} onClick={() => setEditOrgId(org.id)} onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setEditOrgId(org.id);
              }
            }} className="block p-6 rounded-lg border border-purple-700/50 bg-slate-800/50 hover:bg-slate-700/50 hover:border-purple-500/50 transition-all shadow-lg cursor-pointer">
                    <div className="flex justify-between items-start gap-4">
                      <div className="min-w-0 flex-1">
                        <h3 className="text-lg font-semibold text-purple-300 mb-2">{org.name}</h3>
                        <p className="text-sm text-slate-400 mb-1">
                          Status: <span className={`font-medium ${org.status === 'active' ? 'text-green-400' : 'text-red-400'}`}>{org.status}</span>
                        </p>
                        {org.contact_email && <p className="text-sm text-slate-400 mb-1">Contact: {org.contact_email}</p>}
                        <p className="text-sm text-slate-400 mb-1">Auth: {org.auth_architecture ?? 'N/A'}</p>
                        {(org.domain ?? org.cognito_domain) && <p className="text-sm text-slate-400">Domain: {org.domain ?? org.cognito_domain}</p>}
                        {org.tenant_count != null && <p className="text-sm text-slate-400 mt-1">Tenants: {org.tenant_count}</p>}
                        {org.status_message != null && org.status_message !== '' && <p className={`text-sm mt-1 ${statusMessageClassName(org.status_class)}`}>
                            {org.status_message}
                          </p>}
                      </div>
                      <div className="flex flex-col gap-2 shrink-0" onClick={e => e.stopPropagation()}>
                        {!!org.can_add_tenants && <Button size="sm" color="primary" onPress={() => setCreateTenantOrg(org)} className="text-white" aria-label={`Create tenant in ${org.name}`} startContent={<PlusIcon size={16} />}>
                            Create Tenant
                          </Button>}
                        {!!org.can_add_tenants && org.auth_architecture === 'cognito-single-pool' && <Button size="sm" color="primary" onPress={() => setOrgToSetNotReady(org)} className="text-white" aria-label={`Set ${org.name} as not ready for tenants`} startContent={<ProhibitIcon size={16} />}>
                              Set not ready for tenants
                            </Button>}
                        {org.auth_architecture === 'cognito-single-pool' && !org.is_current_organization && (org.tenant_count ?? 0) < 20 && <Button size="sm" variant="bordered" onPress={() => setOrgToSetCurrent(org)} className="border-white/20 text-white hover:bg-white/10" aria-label={`Set ${org.name} as current organization`}>
                            Set as current
                          </Button>}
                        {org.auth_architecture === 'cognito-single-pool' && org.is_current_organization && !org.knowledge_base_ready && <Button size="sm" variant="bordered" onPress={() => setOrgToSetReady(org)} className="border-white/20 text-white hover:bg-white/10" aria-label={`Set ${org.name} as ready for tenants`}>
                            Set ready for tenants
                          </Button>}
                        <Button size="sm" variant="bordered" onPress={() => setEditOrgId(org.id)} className="border-white/20 text-white hover:bg-white/10" aria-label={`Edit ${org.name}`} startContent={<PencilSimpleIcon size={16} />}>
                          Edit
                        </Button>
                      </div>
                    </div>
                  </div>)}
              </div>
            </CardBody>
          </Card>}
      </div>

      <Modal isOpen={isOpen} onOpenChange={onOpenChange} isDismissable={false} classNames={{
      base: 'bg-slate-900 border border-purple-700/50 rounded-xl shadow-2xl',
      header: 'border-b border-purple-500/20 text-purple-300',
      body: 'text-white',
      closeButton: 'text-purple-300 hover:text-white hover:bg-purple-500/20'
    }} data-sentry-element="Modal" data-sentry-source-file="OrganizationsTab.tsx">
        <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="OrganizationsTab.tsx">
          {onClose => <>
              <ModalHeader className="flex flex-col gap-1 text-purple-300 text-xl font-bold">
                Create Organization
              </ModalHeader>
              <ModalBody>
                <CreateOrganizationForm onSubmit={handleSubmitCreate} onSuccess={org => {
              handleCreateSuccess(org);
              onClose();
            }} onCancel={onClose} />
              </ModalBody>
            </>}
        </ModalContent>
      </Modal>

      <Modal isOpen={createTenantOrg != null} onOpenChange={open => {
      if (!open) setCreateTenantOrg(null);
    }} classNames={{
      base: 'bg-slate-900 border border-purple-700/50 rounded-xl shadow-2xl',
      header: 'border-b border-purple-500/20 text-purple-300',
      body: 'text-white',
      closeButton: 'text-purple-300 hover:text-white hover:bg-purple-500/20'
    }} data-sentry-element="Modal" data-sentry-source-file="OrganizationsTab.tsx">
        <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="OrganizationsTab.tsx">
          {onClose => <>
              <ModalHeader className="flex flex-col gap-1 text-purple-300 text-xl font-bold">
                Create Tenant in {createTenantOrg?.name}
              </ModalHeader>
              <ModalBody>
                <CreateTenantForm idToken={idToken ?? null} defaultOrganizationId={createTenantOrg?.id} defaultOrganizationName={createTenantOrg?.name} defaultOrganizationAuthArchitecture={createTenantOrg?.auth_architecture} onSubmit={handleSubmitCreateTenant} onSuccess={tenant => {
              handleCreateTenantSuccess(tenant);
              onClose();
            }} onCancel={onClose} />
              </ModalBody>
            </>}
        </ModalContent>
      </Modal>

      <Modal isOpen={editOrgId != null} onOpenChange={open => {
      if (!open) {
        setEditOrgId(null);
        setEditOrg(null);
        setEditError(null);
      }
    }} size="2xl" scrollBehavior="inside" classNames={{
      base: 'bg-slate-900 border border-purple-700/50 rounded-xl shadow-2xl max-h-[90vh] flex flex-col',
      header: 'border-b border-purple-500/20 text-purple-300 shrink-0',
      body: 'text-white overflow-y-auto min-h-0 flex-1',
      closeButton: 'text-purple-300 hover:text-white hover:bg-purple-500/20'
    }} data-sentry-element="Modal" data-sentry-source-file="OrganizationsTab.tsx">
        <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="OrganizationsTab.tsx">
          {onClose => <>
              <ModalHeader className="flex flex-col gap-1 text-purple-300 text-xl font-bold">
                Edit Organization
              </ModalHeader>
              <ModalBody>
                {editLoading && <div className="flex flex-col items-center justify-center gap-2 py-8">
                    <Spinner size="lg" color="secondary" />
                    <p className="text-gray-300">Loading organization...</p>
                  </div>}
                {!editLoading && editError && <div className="space-y-4">
                    <p role="alert" className="text-red-400">{editError}</p>
                    <Button onPress={onClose} className="text-white">Close</Button>
                  </div>}
                {!editLoading && !editError && editOrg && <EditOrganizationForm organization={editOrg} onSubmit={handleSubmitEdit} onSuccess={org => {
              handleEditSuccess(org);
              onClose();
            }} onCancel={onClose} />}
              </ModalBody>
            </>}
        </ModalContent>
      </Modal>

      <Modal isOpen={orgToSetCurrent != null} onOpenChange={open => {
      if (!open) setOrgToSetCurrent(null);
    }} classNames={{
      base: 'bg-slate-900 border border-purple-700/50 rounded-xl shadow-2xl',
      header: 'border-b border-purple-500/20 text-purple-300',
      body: 'text-white',
      footer: 'border-t border-purple-500/20',
      closeButton: 'text-purple-300 hover:text-white hover:bg-purple-500/20'
    }} data-sentry-element="Modal" data-sentry-source-file="OrganizationsTab.tsx">
        <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="OrganizationsTab.tsx">
          {onClose => <>
              <ModalHeader className="flex flex-col gap-1 text-purple-300 text-xl font-bold">
                Set as current organization?
              </ModalHeader>
              <ModalBody>
                {orgToSetCurrent && <p className="text-slate-300">
                    Are you sure you want to set <strong className="text-white">{orgToSetCurrent.name}</strong> as the
                    current organization? New tenants will be created in this org until it reaches the tenant limit.
                  </p>}
              </ModalBody>
              <ModalFooter className="gap-2">
                <Button variant="bordered" onPress={onClose} isDisabled={setCurrentLoading} className="border-white/20 text-white hover:bg-white/10">
                  Cancel
                </Button>
                <Button color="primary" onPress={() => handleConfirmSetCurrent(onClose)} isLoading={setCurrentLoading} className="text-white">
                  Set as current
                </Button>
              </ModalFooter>
            </>}
        </ModalContent>
      </Modal>

      <Modal isOpen={orgToSetReady != null} onOpenChange={open => {
      if (!open) setOrgToSetReady(null);
    }} classNames={{
      base: 'bg-slate-900 border border-purple-700/50 rounded-xl shadow-2xl',
      header: 'border-b border-purple-500/20 text-purple-300',
      body: 'text-white',
      footer: 'border-t border-purple-500/20',
      closeButton: 'text-purple-300 hover:text-white hover:bg-purple-500/20'
    }} data-sentry-element="Modal" data-sentry-source-file="OrganizationsTab.tsx">
        <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="OrganizationsTab.tsx">
          {onClose => <>
              <ModalHeader className="flex flex-col gap-1 text-purple-300 text-xl font-bold">
                Set as ready for tenants?
              </ModalHeader>
              <ModalBody>
                {orgToSetReady && <p className="text-slate-300">
                    Mark <strong className="text-white">{orgToSetReady.name}</strong> as ready for tenants? You will
                    then be able to create new tenants in this organization.
                  </p>}
              </ModalBody>
              <ModalFooter className="gap-2">
                <Button variant="bordered" onPress={onClose} isDisabled={setReadyLoading} className="border-white/20 text-white hover:bg-white/10">
                  Cancel
                </Button>
                <Button color="primary" onPress={() => handleConfirmSetReady(onClose)} isLoading={setReadyLoading} className="text-white">
                  Set as ready
                </Button>
              </ModalFooter>
            </>}
        </ModalContent>
      </Modal>

      <Modal isOpen={orgToSetNotReady != null} onOpenChange={open => {
      if (!open) setOrgToSetNotReady(null);
    }} classNames={{
      base: 'bg-slate-900 border border-purple-700/50 rounded-xl shadow-2xl',
      header: 'border-b border-purple-500/20 text-purple-300',
      body: 'text-white',
      footer: 'border-t border-purple-500/20',
      closeButton: 'text-purple-300 hover:text-white hover:bg-purple-500/20'
    }} data-sentry-element="Modal" data-sentry-source-file="OrganizationsTab.tsx">
        <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="OrganizationsTab.tsx">
          {onClose => <>
                <ModalHeader className="flex flex-col gap-1 text-purple-300 text-xl font-bold">
                  Set as not ready for tenants?
                </ModalHeader>
                <ModalBody>
                  {orgToSetNotReady && <p className="text-slate-300">
                        Mark <strong className="text-white">{orgToSetNotReady.name}</strong> as not ready for tenants? You will
                        not be able to create new tenants in this organization.
                      </p>}
                </ModalBody>
                <ModalFooter className="gap-2">
                  <Button variant="bordered" onPress={onClose} isDisabled={setNotReadyLoading} className="border-white/20 text-white hover:bg-white/10">
                    Cancel
                  </Button>
                  <Button color="primary" onPress={() => handleConfirmSetNotReady(onClose)} isLoading={setNotReadyLoading} className="text-white">
                    Set as not ready
                  </Button>
                </ModalFooter>
              </>}
        </ModalContent>
      </Modal>
    </>;
}